import cx from 'classnames';
import {map, take} from 'lodash';
import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, InformationModal, Table, TableBody, TableCell, TableRow} from 'components';
import {UserContext} from 'context';
import {BillingPlatformInvoiceStatus} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformInvoice} from 'types';
import {formatDate, transformUtils} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.INVOICES;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

const INITIAL_ITEM_COUNT = 10;

type Props = {
    invoices: BillingPlatformInvoice[];
}

export const InvoicesTable: React.FC<Props> = ({invoices}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);

    const hasViewAllBtn = useMemo(() => invoices?.length > INITIAL_ITEM_COUNT, [invoices]);
    const initialInvoices = useMemo(() => take(invoices, INITIAL_ITEM_COUNT), [invoices]);
    const [modalOpen, setModalOpen] = useState(false);

    const headers = useMemo(() => [
        t(T_KEYS.COLUMNS.DATE),
        t(T_KEYS.COLUMNS.PLAN_NAME),
        t(T_KEYS.COLUMNS.PLAN_QUANTITY),
        t(T_KEYS.COLUMNS.STATUS),
        t(T_KEYS.COLUMNS.AMOUNT),
    ], [t]);

    const formatPaymentDate = useCallback((paymentDate: Date) => formatDate(paymentDate, timeZone, dateFormat), [timeZone, dateFormat]);

    const renderInvoiceTable = useCallback((items: BillingPlatformInvoice[]) => (
        <Table noData={!items?.length} headerNames={headers}>
            <TableBody>
                {map(items, (payment, index) => (
                    <TableRow key={index}>
                        <TableCell>{formatPaymentDate(payment.date)}</TableCell>
                        <TableCell>{payment.planName}</TableCell>
                        <TableCell>{payment.planQuantity}</TableCell>
                        <TableCell className={cx({
                            'tw-text-success': payment.status === BillingPlatformInvoiceStatus.Paid,
                            'tw-text-alert': [
                                BillingPlatformInvoiceStatus.NotPaid,
                                BillingPlatformInvoiceStatus.PaymentDue
                            ].includes(payment.status)
                        })}
                        >
                            {payment.status}
                        </TableCell>
                        <TableCell className='tw-font-bold'>{transformUtils.formatPrice(payment.total, payment.currency)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    ), [formatPaymentDate, headers]);

    return (
        <div className='-sm:tw-hidden'>
            {renderInvoiceTable(initialInvoices)}
            {hasViewAllBtn &&
                <div className='tw-text-right'>
                    <Button className='tw-mt-4' onClick={() => setModalOpen(true)}>{t(T_KEYS_COMMON.BUTTONS.VIEW_ALL)}</Button>
                </div>}
            <InformationModal
                title={t(T_KEYS.VIEW_ALL_MODAL.TITLE)}
                open={modalOpen}
                maxWidth='md'
                fullWidth
                onClose={() => setModalOpen(false)}
            >
                {renderInvoiceTable(invoices)}
            </InformationModal>
        </div>
    );
};